import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"

import {
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  exportReportToCsv,
  exportReportToExcel,
  exportReportToPdf,
} from "../../utils/reportExportUtils"

import {
  EmptyState,
  PageHeader,
  SectionCard,
} from "../../components/ui"

import {
  getAuditVariancesReport,
  getFinancialSummaryReport,
  getLowStockReport,
  getOpenDebtsReport,
  getReportingFormData,
  getSalesByLocationReport,
  getSalesSummaryReport,
  getStockCurrentReport,
  getStockMovementsReport,
  getStoreReconciliationReport,
  getTopClientsReport,
  getTopProductsReport,
  getTopStoresReport,
} from "../../services/reportingService"


// =========================================================
// CONSTANTS
// =========================================================

const today =
  new Date()

const toDateInput = (
  date
) =>
  date
    .toISOString()
    .slice(
      0,
      10
    )

const defaultFilters = {
  date_from:
    toDateInput(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      )
    ),

  date_to:
    toDateInput(
      today
    ),

  store_id:
    "",

  warehouse_id:
    "",

  location_level:
    "city",

  limit:
    25,

  threshold:
    10,
}


const REPORT_GROUPS = [
  {
    key:
      "sales",

    label:
      "Ventes",

    description:
      "Analyse commerciale, produits, clients, points de vente et zones géographiques.",

    reports: [
      {
        key:
          "sales_summary",

        label:
          "Synthèse des ventes",

        description:
          "CA, encaissements, créances et évolution temporelle.",
      },

      {
        key:
          "top_products",

        label:
          "Top produits",

        description:
          "Produits les plus vendus et montants générés.",
      },

      {
        key:
          "top_clients",

        label:
          "Top clients",

        description:
          "Clients les plus importants par chiffre d'affaires.",
      },

      {
        key:
          "top_stores",

        label:
          "Performance des shops",

        description:
          "Comparaison des points de vente.",
      },

      {
        key:
          "sales_location",

        label:
          "Ventes par localisation",

        description:
          "Répartition géographique des ventes.",
      },
    ],
  },

  {
    key:
      "stock",

    label:
      "Stock",

    description:
      "État du stock, alertes, mouvements et écarts d'inventaire.",

    reports: [
      {
        key:
          "stock_current",

        label:
          "Stock actuel",

        description:
          "Quantités présentes, réservées et disponibles.",
      },

      {
        key:
          "low_stock",

        label:
          "Stock faible",

        description:
          "Produits sous le seuil de réapprovisionnement.",
      },

      {
        key:
          "stock_movements",

        label:
          "Mouvements de stock",

        description:
          "Entrées, sorties et références des mouvements.",
      },

      {
        key:
          "audit_variances",

        label:
          "Écarts d'inventaire",

        description:
          "Différences entre quantités système et physiques.",
      },
    ],
  },

  {
    key:
      "finance",

    label:
      "Finance & contrôle",

    description:
      "Trésorerie, dépenses, dettes et rapprochements.",

    reports: [
      {
        key:
          "financial_summary",

        label:
          "Synthèse financière",

        description:
          "Ventes, encaissé, dépôts, dépenses et écarts.",
      },

      {
        key:
          "open_debts",

        label:
          "Dettes ouvertes",

        description:
          "Dettes restant à régler et échéances.",
      },

      {
        key:
          "reconciliations",

        label:
          "Rapprochements shops",

        description:
          "Contrôle ventes, dépenses, dépôts et écarts.",
      },
    ],
  },
]


// =========================================================
// HELPERS
// =========================================================

const toNumber = (
  value
) => {
  const number =
    Number(
      value || 0
    )

  return Number.isFinite(
    number
  )
    ? number
    : 0
}


const money = (
  value
) =>
  toNumber(
    value
  ).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits:
        2,

      maximumFractionDigits:
        2,
    }
  )


const qty = (
  value
) =>
  toNumber(
    value
  ).toLocaleString(
    "fr-FR",
    {
      maximumFractionDigits:
        2,
    }
  )


const formatDate = (
  value
) => {
  if (
    !value
  ) {
    return "-"
  }

  const date =
    new Date(
      value
    )

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(
      value
    )
  }

  return date.toLocaleDateString(
    "fr-FR"
  )
}




// =========================================================
// REPORT TABLE
// =========================================================

function ReportTable({
  columns,
  rows,
}) {
  if (
    !rows.length
  ) {
    return (
      <EmptyState
        title="Aucune donnée"
        description="Aucune ligne ne correspond aux filtres sélectionnés."
        minHeight={220}
      />
    )
  }

  return (
    <Box
      sx={{
        width:
          "100%",

        overflowX:
          "auto",
      }}
    >
      <Box
        component="table"

        sx={{
          width:
            "100%",

          minWidth:
            820,

          borderCollapse:
            "collapse",

          "& th":
            {
              textAlign:
                "left",

              fontSize:
                12,

              fontWeight:
                900,

              color:
                "text.secondary",

              backgroundColor:
                "#f8fafc",

              py:
                1.25,

              px:
                1.5,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              whiteSpace:
                "nowrap",
            },

          "& td":
            {
              fontSize:
                13,

              py:
                1.2,

              px:
                1.5,

              borderBottom:
                "1px solid",

              borderColor:
                "#eef2f7",

              whiteSpace:
                "nowrap",
            },

          "& tbody tr:hover":
            {
              backgroundColor:
                "#f8fbff",
            },
        }}
      >
        <Box
          component="thead"
        >
          <Box
            component="tr"
          >
            {columns.map(
              (
                column
              ) => (
                <Box
                  component="th"

                  key={
                    column.key
                  }
                >
                  {column.label}
                </Box>
              )
            )}
          </Box>
        </Box>

        <Box
          component="tbody"
        >
          {rows.map(
            (
              row,
              index
            ) => (
              <Box
                component="tr"

                key={
                  row.id ||
                  row.product_id ||
                  row.customer_id ||
                  row.store_id ||
                  row.reconciliation_code ||
                  `${index}`
                }
              >
                {columns.map(
                  (
                    column
                  ) => (
                    <Box
                      component="td"

                      key={
                        column.key
                      }
                    >
                      {column.render
                        ? column.render(
                            row
                          )
                        : row[
                            column.key
                          ] ??
                          "-"}
                    </Box>
                  )
                )}
              </Box>
            )
          )}
        </Box>
      </Box>
    </Box>
  )
}


// =========================================================
// REPORTING PAGE
// =========================================================

function ReportingPage() {
  const [
    activeGroup,
    setActiveGroup,
  ] = useState(
    "sales"
  )

  const [
    activeReport,
    setActiveReport,
  ] = useState(
    "sales_summary"
  )

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    locationLevels,
    setLocationLevels,
  ] = useState([
    {
      value:
        "city",

      label:
        "Ville",
    },
  ])

  const [
    filters,
    setFilters,
  ] = useState(
    defaultFilters
  )

  const [
    result,
    setResult,
  ] = useState(
    null
  )

  const [
    loading,
    setLoading,
  ] = useState(
    false
  )

  const [
    errorMessage,
    setErrorMessage,
  ] = useState(
    ""
  )


  // =======================================================
  // FORM DATA
  // =======================================================

  useEffect(
    () => {
      const loadFormData =
        async () => {
          try {
            const data =
              await getReportingFormData()

            setStores(
              Array.isArray(
                data?.stores
              )
                ? data.stores
                : []
            )

            setWarehouses(
              Array.isArray(
                data?.warehouses
              )
                ? data.warehouses
                : []
            )

            if (
              Array.isArray(
                data?.location_levels
              ) &&
              data.location_levels.length
            ) {
              setLocationLevels(
                data.location_levels
              )
            }
          } catch (
            error
          ) {
            console.error(
              error
            )
          }
        }

      loadFormData()
    },
    []
  )


  // =======================================================
  // REPORT META
  // =======================================================

  const currentGroup =
    useMemo(
      () =>
        REPORT_GROUPS.find(
          (
            group
          ) =>
            group.key ===
            activeGroup
        ) ||
        REPORT_GROUPS[0],
      [
        activeGroup,
      ]
    )


  const currentReport =
    useMemo(
      () =>
        currentGroup.reports.find(
          (
            report
          ) =>
            report.key ===
            activeReport
        ) ||
        currentGroup.reports[0],
      [
        currentGroup,
        activeReport,
      ]
    )


  // =======================================================
  // FILTERS
  // =======================================================

  const handleFilterChange =
    (
      event
    ) => {
      const {
        name,
        value,
      } =
        event.target

      setFilters(
        (
          current
        ) => ({
          ...current,

          [name]:
            value,
        })
      )
    }


  const resetFilters =
    () => {
      setFilters(
        defaultFilters
      )

      setResult(
        null
      )
    }


  // =======================================================
  // LOAD ACTIVE REPORT
  // =======================================================

  const loadReport =
    async () => {
      try {
        setLoading(
          true
        )

        setErrorMessage(
          ""
        )

        const commonParams = {
          date_from:
            filters.date_from ||
            undefined,

          date_to:
            filters.date_to ||
            undefined,

          store_id:
            filters.store_id ||
            undefined,

          limit:
            Number(
              filters.limit ||
                25
            ),
        }

        let data

        switch (
          activeReport
        ) {
          case "sales_summary":
            data =
              await getSalesSummaryReport(
                commonParams
              )
            break

          case "top_products":
            data =
              await getTopProductsReport(
                commonParams
              )
            break

          case "top_clients":
            data =
              await getTopClientsReport(
                commonParams
              )
            break

          case "top_stores":
            data =
              await getTopStoresReport({
                date_from:
                  commonParams.date_from,

                date_to:
                  commonParams.date_to,

                limit:
                  commonParams.limit,
              })
            break

          case "sales_location":
            data =
              await getSalesByLocationReport({
                date_from:
                  commonParams.date_from,

                date_to:
                  commonParams.date_to,

                location_level:
                  filters.location_level,

                limit:
                  commonParams.limit,
              })
            break

          case "stock_current":
            data =
              await getStockCurrentReport({
                store_id:
                  filters.store_id ||
                  undefined,

                warehouse_id:
                  filters.warehouse_id ||
                  undefined,

                limit:
                  commonParams.limit,
              })
            break

          case "low_stock":
            data =
              await getLowStockReport({
                store_id:
                  filters.store_id ||
                  undefined,

                warehouse_id:
                  filters.warehouse_id ||
                  undefined,

                threshold:
                  Number(
                    filters.threshold ||
                      10
                  ),

                limit:
                  commonParams.limit,
              })
            break

          case "stock_movements":
            data =
              await getStockMovementsReport({
                ...commonParams,

                warehouse_id:
                  filters.warehouse_id ||
                  undefined,
              })
            break

          case "audit_variances":
            data =
              await getAuditVariancesReport({
                limit:
                  commonParams.limit,
              })
            break

          case "financial_summary":
            data =
              await getFinancialSummaryReport(
                commonParams
              )
            break

          case "open_debts":
            data =
              await getOpenDebtsReport(
                commonParams
              )
            break

          case "reconciliations":
            data =
              await getStoreReconciliationReport(
                commonParams
              )
            break

          default:
            data =
              null
        }

        setResult(
          data
        )
      } catch (
        error
      ) {
        console.error(
          error
        )

        setResult(
          null
        )

        setErrorMessage(
          error?.response?.data?.detail ||
            "Impossible de charger ce rapport."
        )
      } finally {
        setLoading(
          false
        )
      }
    }


  useEffect(
    () => {
      loadReport()
    },
    [
      activeReport,
    ]
  )


  // =======================================================
  // REPORT PRESENTATION
  // =======================================================

  const reportPresentation =
    useMemo(
      () => {
        switch (
          activeReport
        ) {
          case "sales_summary": {
            const summary =
              result?.summary ||
              {}

            return {
              cards: [
                {
                  label:
                    "Ventes",

                  value:
                    qty(
                      summary.total_sales_count
                    ),
                },

                {
                  label:
                    "Chiffre d'affaires",

                  value:
                    money(
                      summary.total_sales_amount
                    ),
                },

                {
                  label:
                    "Encaissé",

                  value:
                    money(
                      summary.total_paid_amount
                    ),
                },

                {
                  label:
                    "Reste clients",

                  value:
                    money(
                      summary.total_remaining_amount
                    ),
                },
              ],

              rows:
                result?.daily_sales ||
                [],

              columns: [
                {
                  key:
                    "sale_day",

                  label:
                    "Date",

                  render:
                    (
                      row
                    ) =>
                      formatDate(
                        row.sale_day
                      ),
                },

                {
                  key:
                    "sales_count",

                  label:
                    "Ventes",
                },

                {
                  key:
                    "total_amount",

                  label:
                    "Montant",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_amount
                      ),
                },

                {
                  key:
                    "paid_amount",

                  label:
                    "Encaissé",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.paid_amount
                      ),
                },

                {
                  key:
                    "remaining_amount",

                  label:
                    "Reste",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.remaining_amount
                      ),
                },
              ],
            }
          }


          case "top_products":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "product_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "product_name",

                  label:
                    "Produit",
                },

                {
                  key:
                    "total_quantity",

                  label:
                    "Quantité",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.total_quantity
                      ),
                },

                {
                  key:
                    "total_sales_amount",

                  label:
                    "Montant",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_sales_amount
                      ),
                },
              ],
            }


          case "top_clients":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "customer_name",

                  label:
                    "Client",
                },

                {
                  key:
                    "customer_phone",

                  label:
                    "Téléphone",
                },

                {
                  key:
                    "sales_count",

                  label:
                    "Ventes",
                },

                {
                  key:
                    "total_sales_amount",

                  label:
                    "Montant",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_sales_amount
                      ),
                },

                {
                  key:
                    "total_remaining_amount",

                  label:
                    "Reste",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_remaining_amount
                      ),
                },
              ],
            }


          case "top_stores":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "store_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "sales_count",

                  label:
                    "Ventes",
                },

                {
                  key:
                    "total_sales_amount",

                  label:
                    "CA",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_sales_amount
                      ),
                },

                {
                  key:
                    "total_paid_amount",

                  label:
                    "Encaissé",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_paid_amount
                      ),
                },

                {
                  key:
                    "total_remaining_amount",

                  label:
                    "Reste",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_remaining_amount
                      ),
                },
              ],
            }


          case "sales_location":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "location_name",

                  label:
                    "Localisation",
                },

                {
                  key:
                    "sales_count",

                  label:
                    "Ventes",
                },

                {
                  key:
                    "total_sales_amount",

                  label:
                    "Montant",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_sales_amount
                      ),
                },
              ],
            }


          case "stock_current":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "product_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "product_name",

                  label:
                    "Produit",
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "warehouse_name",

                  label:
                    "Entrepôt",
                },

                {
                  key:
                    "stock_quantity",

                  label:
                    "Quantité",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.stock_quantity
                      ),
                },

                {
                  key:
                    "reserved_quantity",

                  label:
                    "Réservé",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.reserved_quantity
                      ),
                },

                {
                  key:
                    "available_quantity",

                  label:
                    "Disponible",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.available_quantity
                      ),
                },
              ],
            }


          case "low_stock":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "product_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "product_name",

                  label:
                    "Produit",
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "warehouse_name",

                  label:
                    "Entrepôt",
                },

                {
                  key:
                    "stock_quantity",

                  label:
                    "Stock",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.stock_quantity
                      ),
                },

                {
                  key:
                    "reorder_level",

                  label:
                    "Seuil",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.reorder_level
                      ),
                },
              ],
            }


          case "stock_movements":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "created_at",

                  label:
                    "Date",

                  render:
                    (
                      row
                    ) =>
                      formatDate(
                        row.created_at
                      ),
                },

                {
                  key:
                    "product_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "product_name",

                  label:
                    "Produit",
                },

                {
                  key:
                    "movement_type",

                  label:
                    "Mouvement",
                },

                {
                  key:
                    "quantity",

                  label:
                    "Quantité",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.quantity
                      ),
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "warehouse_name",

                  label:
                    "Entrepôt",
                },

                {
                  key:
                    "external_reference",

                  label:
                    "Référence",
                },
              ],
            }


          case "audit_variances":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "audit_code",

                  label:
                    "Audit",
                },

                {
                  key:
                    "product_code",

                  label:
                    "Produit",
                },

                {
                  key:
                    "system_quantity",

                  label:
                    "Système",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.system_quantity
                      ),
                },

                {
                  key:
                    "physical_quantity",

                  label:
                    "Physique",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.physical_quantity
                      ),
                },

                {
                  key:
                    "variance_quantity",

                  label:
                    "Écart",

                  render:
                    (
                      row
                    ) =>
                      qty(
                        row.variance_quantity
                      ),
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "warehouse_name",

                  label:
                    "Entrepôt",
                },
              ],
            }


          case "financial_summary":
            return {
              cards: [
                {
                  label:
                    "Ventes",

                  value:
                    money(
                      result?.total_sales
                    ),
                },

                {
                  label:
                    "Encaissé",

                  value:
                    money(
                      result?.total_paid
                    ),
                },

                {
                  label:
                    "Dépôts",

                  value:
                    money(
                      result?.total_deposits
                    ),
                },

                {
                  label:
                    "Dépenses",

                  value:
                    money(
                      result?.total_expenses
                    ),
                },

                {
                  label:
                    "Cash attendu",

                  value:
                    money(
                      result?.expected_cash
                    ),
                },

                {
                  label:
                    "Écart",

                  value:
                    money(
                      result?.variance
                    ),
                },
              ],

              rows:
                [],

              columns:
                [],
            }


          case "open_debts":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "debt_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "debt_type",

                  label:
                    "Type",
                },

                {
                  key:
                    "customer_name",

                  label:
                    "Client",
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "original_amount",

                  label:
                    "Initial",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.original_amount
                      ),
                },

                {
                  key:
                    "paid_amount",

                  label:
                    "Payé",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.paid_amount
                      ),
                },

                {
                  key:
                    "remaining_amount",

                  label:
                    "Restant",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.remaining_amount
                      ),
                },

                {
                  key:
                    "due_date",

                  label:
                    "Échéance",

                  render:
                    (
                      row
                    ) =>
                      formatDate(
                        row.due_date
                      ),
                },

                {
                  key:
                    "status",

                  label:
                    "Statut",
                },
              ],
            }


          case "reconciliations":
            return {
              rows:
                Array.isArray(
                  result
                )
                  ? result
                  : [],

              columns: [
                {
                  key:
                    "reconciliation_code",

                  label:
                    "Code",
                },

                {
                  key:
                    "store_name",

                  label:
                    "Shop",
                },

                {
                  key:
                    "period_start",

                  label:
                    "Début",

                  render:
                    (
                      row
                    ) =>
                      formatDate(
                        row.period_start
                      ),
                },

                {
                  key:
                    "period_end",

                  label:
                    "Fin",

                  render:
                    (
                      row
                    ) =>
                      formatDate(
                        row.period_end
                      ),
                },

                {
                  key:
                    "total_sales",

                  label:
                    "Ventes",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_sales
                      ),
                },

                {
                  key:
                    "total_deposits",

                  label:
                    "Dépôts",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_deposits
                      ),
                },

                {
                  key:
                    "total_expenses",

                  label:
                    "Dépenses",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.total_expenses
                      ),
                },

                {
                  key:
                    "variance_amount",

                  label:
                    "Écart",

                  render:
                    (
                      row
                    ) =>
                      money(
                        row.variance_amount
                      ),
                },

                {
                  key:
                    "status",

                  label:
                    "Statut",
                },
              ],
            }


          default:
            return {
              rows:
                [],

              columns:
                [],
            }
        }
      },
      [
        activeReport,
        result,
      ]
    )


  const showDateFilters =
    ![
      "stock_current",
      "low_stock",
      "audit_variances",
    ].includes(
      activeReport
    )


  const showStoreFilter =
    ![
      "top_stores",
      "sales_location",
      "audit_variances",
    ].includes(
      activeReport
    )


  const showWarehouseFilter =
    [
      "stock_current",
      "low_stock",
      "stock_movements",
    ].includes(
      activeReport
    )


  const showLocationLevel =
    activeReport ===
    "sales_location"


  const showThreshold =
    activeReport ===
    "low_stock"



  const selectedStore =
    useMemo(
      () =>
        stores.find(
          (
            store
          ) =>
            String(
              store.id
            ) ===
            String(
              filters.store_id
            )
        ) ||
        null,
      [
        stores,
        filters.store_id,
      ]
    )


  const selectedWarehouse =
    useMemo(
      () =>
        warehouses.find(
          (
            warehouse
          ) =>
            String(
              warehouse.id
            ) ===
            String(
              filters.warehouse_id
            )
        ) ||
        null,
      [
        warehouses,
        filters.warehouse_id,
      ]
    )


  const exportPayload =
    useMemo(
      () => ({
        appName:
          "RLSys Business",

        appSubtitle:
          "Retail & Logistics System",

        reportGroup:
          currentGroup.label,

        reportTitle:
          currentReport.label,

        reportDescription:
          currentReport.description,

        periodLabel:
          showDateFilters
            ? `${filters.date_from || "Début"} → ${filters.date_to || "Aujourd'hui"}`
            : "Situation actuelle",

        generatedAt:
          new Date(),

        generatedBy:
          localStorage.getItem(
            "kongosys_full_name"
          ) ||
          "Utilisateur",

        context: {
          "Shop":
            selectedStore
              ? `${selectedStore.code || ""}${selectedStore.code ? " - " : ""}${selectedStore.name || ""}`
              : showStoreFilter
                ? "Tous les shops"
                : "Non applicable",

          "Entrepôt":
            selectedWarehouse
              ? `${selectedWarehouse.code || ""}${selectedWarehouse.code ? " - " : ""}${selectedWarehouse.name || ""}`
              : showWarehouseFilter
                ? "Tous les entrepôts"
                : "Non applicable",

          "Niveau géographique":
            showLocationLevel
              ? locationLevels.find(
                  (
                    item
                  ) =>
                    item.value ===
                    filters.location_level
                )?.label ||
                filters.location_level
              : "Non applicable",

          "Seuil de stock":
            showThreshold
              ? filters.threshold
              : "Non applicable",

          "Limite":
            `${filters.limit} lignes`,
        },

        cards:
          reportPresentation.cards ||
          [],

        columns:
          reportPresentation.columns ||
          [],

        rows:
          reportPresentation.rows ||
          [],
      }),
      [
        currentGroup.label,
        currentReport.label,
        currentReport.description,
        filters,
        locationLevels,
        reportPresentation,
        selectedStore,
        selectedWarehouse,
        showDateFilters,
        showLocationLevel,
        showStoreFilter,
        showThreshold,
        showWarehouseFilter,
      ]
    )


  const hasExportData =
    Boolean(
      reportPresentation.cards?.length ||
      reportPresentation.rows?.length
    )


  const handleExportCsv =
    () => {
      if (
        !hasExportData
      ) {
        return
      }

      exportReportToCsv(
        exportPayload
      )
    }


  const handleExportExcel =
    () => {
      if (
        !hasExportData
      ) {
        return
      }

      exportReportToExcel(
        exportPayload
      )
    }


  const handleExportPdf =
    () => {
      if (
        !hasExportData
      ) {
        return
      }

      exportReportToPdf(
        exportPayload
      )
    }



  // =======================================================
  // UI
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width:
            "100%",

          minWidth:
            0,

          maxWidth:
            "100%",
        }}
      >
        <PageHeader
          eyebrow="Pilotage"
          title="Centre de rapports"
          subtitle="Sélectionnez un rapport, définissez son périmètre, analysez les résultats puis exportez les données pour vos contrôles et réunions."
        />


        {/* ================================================= */}
        {/* REPORT GROUPS */}
        {/* ================================================= */}

        <Paper
          elevation={0}

          sx={{
            mb:
              2,

            border:
              "1px solid",

            borderColor:
              "divider",

            borderRadius:
              3,

            overflow:
              "hidden",
          }}
        >
          <Tabs
            value={
              activeGroup
            }

            onChange={(
              _,
              value
            ) => {
              setActiveGroup(
                value
              )

              const group =
                REPORT_GROUPS.find(
                  (
                    item
                  ) =>
                    item.key ===
                    value
                )

              if (
                group?.reports?.length
              ) {
                setActiveReport(
                  group.reports[0].key
                )
              }
            }}

            variant="scrollable"
            scrollButtons="auto"
          >
            {REPORT_GROUPS.map(
              (
                group
              ) => (
                <Tab
                  key={
                    group.key
                  }
                  value={
                    group.key
                  }
                  label={
                    group.label
                  }
                />
              )
            )}
          </Tabs>
        </Paper>


        {/* ================================================= */}
        {/* REPORT PICKER + FILTERS */}
        {/* ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                lg:
                  "280px minmax(0, 1fr)",
              },

            gap:
              1.5,

            mb:
              2,
          }}
        >
          <Paper
            elevation={0}

            sx={{
              border:
                "1px solid",

              borderColor:
                "divider",

              borderRadius:
                3,

              p:
                1.25,
            }}
          >
            <Typography
              sx={{
                px:
                  1,

                pt:
                  0.4,

                pb:
                  1,

                fontSize:
                  12,

                fontWeight:
                  900,

                color:
                  "text.secondary",

                textTransform:
                  "uppercase",

                letterSpacing:
                  0.8,
              }}
            >
              Rapports disponibles
            </Typography>

            <Stack
              spacing={
                0.65
              }
            >
              {currentGroup.reports.map(
                (
                  report
                ) => (
                  <Button
                    key={
                      report.key
                    }

                    onClick={() =>
                      setActiveReport(
                        report.key
                      )
                    }

                    variant={
                      activeReport ===
                      report.key
                        ? "contained"
                        : "text"
                    }

                    sx={{
                      justifyContent:
                        "flex-start",

                      textAlign:
                        "left",

                      px:
                        1.25,

                      py:
                        1,

                      minHeight:
                        44,

                      borderRadius:
                        2,

                      whiteSpace:
                        "normal",
                    }}
                  >
                    {report.label}
                  </Button>
                )
              )}
            </Stack>
          </Paper>


          <Paper
            elevation={0}

            sx={{
              border:
                "1px solid",

              borderColor:
                "divider",

              borderRadius:
                3,

              p:
                {
                  xs:
                    1.4,

                  sm:
                    1.6,
                },
            }}
          >
            <Stack
              direction={{
                xs:
                  "column",

                md:
                  "row",
              }}

              justifyContent="space-between"

              alignItems={{
                xs:
                  "stretch",

                md:
                  "flex-start",
              }}

              spacing={
                1.5
              }

              sx={{
                mb:
                  1.5,
              }}
            >
              <Box>
                <Typography
                  variant="h6"

                  sx={{
                    fontWeight:
                      900,

                    fontSize:
                      17,
                  }}
                >
                  {currentReport.label}
                </Typography>

                <Typography
                  color="text.secondary"

                  sx={{
                    mt:
                      0.25,

                    fontSize:
                      12.5,
                  }}
                >
                  {currentReport.description}
                </Typography>
              </Box>

              <Stack
                direction={{
                  xs:
                    "column",

                  sm:
                    "row",
                }}

                spacing={
                  1
                }
              >
                <Button
                  variant="outlined"

                  onClick={
                    resetFilters
                  }
                >
                  Réinitialiser
                </Button>

                <Button
                  variant="contained"

                  onClick={
                    loadReport
                  }

                  disabled={
                    loading
                  }
                >
                  {loading
                    ? "Chargement..."
                    : "Générer le rapport"}
                </Button>
              </Stack>
            </Stack>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",

                    xl:
                      "repeat(4, minmax(0, 1fr))",
                  },

                gap:
                  1,
              }}
            >
              {showDateFilters ? (
                <>
                  <TextField
                    label="Date début"
                    name="date_from"
                    type="date"
                    value={
                      filters.date_from
                    }
                    onChange={
                      handleFilterChange
                    }
                    fullWidth
                    slotProps={{
                      inputLabel: {
                        shrink:
                          true,
                      },
                    }}
                  />

                  <TextField
                    label="Date fin"
                    name="date_to"
                    type="date"
                    value={
                      filters.date_to
                    }
                    onChange={
                      handleFilterChange
                    }
                    fullWidth
                    slotProps={{
                      inputLabel: {
                        shrink:
                          true,
                      },
                    }}
                  />
                </>
              ) : null}


              {showStoreFilter ? (
                <TextField
                  select
                  label="Shop"
                  name="store_id"
                  value={
                    filters.store_id
                  }
                  onChange={
                    handleFilterChange
                  }
                  fullWidth
                >
                  <MenuItem value="">
                    Tous les shops
                  </MenuItem>

                  {stores.map(
                    (
                      store
                    ) => (
                      <MenuItem
                        key={
                          store.id
                        }
                        value={
                          store.id
                        }
                      >
                        {store.code} - {store.name}
                      </MenuItem>
                    )
                  )}
                </TextField>
              ) : null}


              {showWarehouseFilter ? (
                <TextField
                  select
                  label="Entrepôt"
                  name="warehouse_id"
                  value={
                    filters.warehouse_id
                  }
                  onChange={
                    handleFilterChange
                  }
                  fullWidth
                >
                  <MenuItem value="">
                    Tous les entrepôts
                  </MenuItem>

                  {warehouses.map(
                    (
                      warehouse
                    ) => (
                      <MenuItem
                        key={
                          warehouse.id
                        }
                        value={
                          warehouse.id
                        }
                      >
                        {warehouse.code} - {warehouse.name}
                      </MenuItem>
                    )
                  )}
                </TextField>
              ) : null}


              {showLocationLevel ? (
                <TextField
                  select
                  label="Niveau géographique"
                  name="location_level"
                  value={
                    filters.location_level
                  }
                  onChange={
                    handleFilterChange
                  }
                  fullWidth
                >
                  {locationLevels.map(
                    (
                      item
                    ) => (
                      <MenuItem
                        key={
                          item.value
                        }
                        value={
                          item.value
                        }
                      >
                        {item.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              ) : null}


              {showThreshold ? (
                <TextField
                  label="Seuil de stock"
                  name="threshold"
                  type="number"
                  value={
                    filters.threshold
                  }
                  onChange={
                    handleFilterChange
                  }
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      min:
                        0,
                    },
                  }}
                />
              ) : null}


              <TextField
                select
                label="Lignes"
                name="limit"
                value={
                  filters.limit
                }
                onChange={
                  handleFilterChange
                }
                fullWidth
              >
                <MenuItem value={10}>
                  10 lignes
                </MenuItem>

                <MenuItem value={25}>
                  25 lignes
                </MenuItem>

                <MenuItem value={50}>
                  50 lignes
                </MenuItem>

                <MenuItem value={100}>
                  100 lignes
                </MenuItem>
              </TextField>
            </Box>
          </Paper>
        </Box>


        {errorMessage ? (
          <Alert
            severity="error"

            sx={{
              mb:
                2,
            }}
          >
            {errorMessage}
          </Alert>
        ) : null}


        {/* ================================================= */}
        {/* SUMMARY CARDS */}
        {/* ================================================= */}

        {reportPresentation.cards?.length ? (
          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns:
                {
                  xs:
                    "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",

                  lg:
                    "repeat(3, minmax(0, 1fr))",

                  xl:
                    "repeat(4, minmax(0, 1fr))",
                },

              gap:
                1.25,

              mb:
                2,
            }}
          >
            {reportPresentation.cards.map(
              (
                card
              ) => (
                <Paper
                  key={
                    card.label
                  }

                  elevation={0}

                  sx={{
                    p:
                      1.7,

                    minHeight:
                      102,

                    border:
                      "1px solid",

                    borderColor:
                      "divider",

                    borderRadius:
                      3,

                    boxShadow:
                      "0 8px 22px rgba(15,23,42,0.035)",
                  }}
                >
                  <Typography
                    color="text.secondary"

                    sx={{
                      fontSize:
                        12.5,

                      fontWeight:
                        800,
                    }}
                  >
                    {card.label}
                  </Typography>

                  <Typography
                    sx={{
                      mt:
                        0.55,

                      fontSize:
                        24,

                      lineHeight:
                        1.1,

                      fontWeight:
                        900,
                    }}
                  >
                    {card.value}
                  </Typography>
                </Paper>
              )
            )}
          </Box>
        ) : null}


        {/* ================================================= */}
        {/* REPORT RESULT */}
        {/* ================================================= */}

        <SectionCard
          title={currentReport.label}
          subtitle={`Résultats du rapport • ${filters.date_from || "début"} → ${filters.date_to || "aujourd'hui"}`}

          actions={
            hasExportData ? (
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={0.8}
                sx={{
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                }}
              >
                {reportPresentation.rows?.length &&
                reportPresentation.columns?.length ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={
                      handleExportCsv
                    }
                    sx={{
                      minWidth: 82,
                    }}
                  >
                    CSV
                  </Button>
                ) : null}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={
                    handleExportExcel
                  }
                  sx={{
                    minWidth: 88,
                  }}
                >
                  Excel
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={
                    handleExportPdf
                  }
                  sx={{
                    minWidth: 82,
                  }}
                >
                  PDF
                </Button>
              </Stack>
            ) : null
          }

          noPadding
        >
          {loading ? (
            <EmptyState
              title="Génération du rapport..."
              description="Les données sont en cours de chargement."
              minHeight={240}
            />
          ) : reportPresentation.rows?.length ||
            reportPresentation.columns?.length ? (
            <ReportTable
              columns={
                reportPresentation.columns ||
                []
              }

              rows={
                reportPresentation.rows ||
                []
              }
            />
          ) : reportPresentation.cards?.length ? (
            <Box
              sx={{
                p: {
                  xs: 2,
                  sm: 2.5,
                },
              }}
            >
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2.5,
                }}
              >
                Ce rapport est une synthèse de pilotage. Les indicateurs ci-dessus
                constituent le résultat du rapport et peuvent être exportés en
                Excel ou PDF.
              </Alert>
            </Box>
          ) : (
            <EmptyState
              title="Aucune donnée"
              description="Aucune ligne ne correspond aux filtres sélectionnés."
              minHeight={240}
            />
          )}
        </SectionCard>
      </Box>
    </MainLayout>
  )
}


export default ReportingPage
